import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { App, Alert, Button, Card, DatePicker, Empty, Flex, Select, Space, Spin, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CompanyContext, UpdateCompanyContext } from "../../context/companyContext";
import { downloadStatsCsv } from "../../utils/statsCsv";
import {
  ApiAccessStatsProvider,
  STATUS_CLASSES,
  StatsGranularity,
  aggregateByBucket,
  useApiAccessStats,
} from "../../context/apiAccessStatsContext";
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;

/** status_class 別の積み上げ色。 */
const STATUS_COLORS: Record<string, string> = {
  "2xx": "#52c41a",
  "3xx": "#1677ff",
  "4xx": "#faad14",
  "5xx": "#f5222d",
  other: "#8c8c8c",
};

/** X 軸ラベル整形 (granularity に応じて時/日)。 */
function axisLabel(bucket: string, granularity: StatsGranularity): string {
  return dayjs(bucket).format(granularity === "day" ? "MM/DD" : "MM/DD HH:mm");
}

function ApiMonitoringPageInner() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { companyOptions } = useContext(CompanyContext);
  const { refreshCompanyOptions } = useContext(UpdateCompanyContext);
  const { keyOptions, series, keysLoading, statsLoading, error, fetchKeys, fetchStats, clearStats } =
    useApiAccessStats();

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [apiKeyId, setApiKeyId] = useState<number | null>(null);
  const [granularity, setGranularity] = useState<StatsGranularity>("hour");
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  useEffect(() => {
    refreshCompanyOptions();
  }, [refreshCompanyOptions]);

  useEffect(() => {
    if (error) message.error(error);
  }, [error, message]);

  const onSelectCompany = useCallback(
    (cid: number) => {
      setCompanyId(cid);
      setApiKeyId(null);
      clearStats();
      fetchKeys(cid);
    },
    [fetchKeys, clearStats]
  );

  const runFetch = useCallback(
    (keyId: number, g: StatsGranularity, r: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
      fetchStats(keyId, {
        granularity: g,
        from: r ? r[0].toISOString() : undefined,
        to: r ? r[1].toISOString() : undefined,
      });
    },
    [fetchStats]
  );

  const onSelectKey = useCallback(
    (keyId: number) => {
      setApiKeyId(keyId);
      runFetch(keyId, granularity, range);
    },
    [runFetch, granularity, range]
  );

  /** プリセット: 直近24時間 (hour) / 直近30日 (day)。 */
  const applyPreset = useCallback(
    (g: StatsGranularity) => {
      const to = dayjs();
      const from = g === "day" ? to.subtract(30, "day") : to.subtract(24, "hour");
      const r: [dayjs.Dayjs, dayjs.Dayjs] = [from, to];
      setGranularity(g);
      setRange(r);
      if (apiKeyId != null) runFetch(apiKeyId, g, r);
    },
    [apiKeyId, runFetch]
  );

  const onApply = useCallback(() => {
    if (apiKeyId == null) {
      message.warning(t("apiMonitoring.selectApiKeyWarning"));
      return;
    }
    runFetch(apiKeyId, granularity, range);
  }, [apiKeyId, granularity, range, runFetch, message, t]);

  const buckets = useMemo(() => aggregateByBucket(series), [series]);

  const selectedKey = useMemo(() => keyOptions.find((k) => k.id === apiKeyId) ?? null, [keyOptions, apiKeyId]);

  const onExport = useCallback(() => {
    if (!selectedKey || series.length === 0) {
      message.warning(t("apiMonitoring.noStatsWarning"));
      return;
    }
    downloadStatsCsv(series, selectedKey.key_prefix);
  }, [selectedKey, series, message, t]);

  const hasData = buckets.length > 0;

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>{t("apiMonitoring.title")}</Typography.Title>

      {/* バックエンド未実装 (#457 集計バッチ) のため本画面で提供できない機能。 */}
      <Alert type="info" showIcon message={t("apiMonitoring.deferredTitle")} description={t("apiMonitoring.deferredNotice")} style={{ marginBottom: 16 }} />

      <Flex align="center" gap="middle" wrap style={{ marginBottom: 16 }}>
        <Select
          showSearch
          placeholder={t("company.selectCompany")}
          style={{ minWidth: 260 }}
          optionFilterProp="label"
          value={companyId ?? undefined}
          onChange={onSelectCompany}
          options={companyOptions.map((c) => ({ label: c.company_name, value: c.id }))}
        />
        <Select
          showSearch
          placeholder={t("apiMonitoring.placeholders.selectApiKey")}
          style={{ minWidth: 260 }}
          optionFilterProp="label"
          loading={keysLoading}
          disabled={companyId == null}
          value={apiKeyId ?? undefined}
          onChange={onSelectKey}
          notFoundContent={companyId == null ? t("apiMonitoring.placeholders.selectCompanyFirst") : t("apiMonitoring.placeholders.noActiveApiKeys")}
          options={keyOptions.map((k) => ({ label: `${k.key_prefix} (${k.name})`, value: k.id }))}
        />
        <Select
          value={granularity}
          style={{ width: 120 }}
          onChange={setGranularity}
          options={[
            { label: t("apiMonitoring.granularity.hour"), value: "hour" },
            { label: t("apiMonitoring.granularity.day"), value: "day" },
          ]}
        />
        <RangePicker
          showTime
          value={range}
          onChange={(v) => setRange(v as [dayjs.Dayjs, dayjs.Dayjs] | null)}
          placeholder={[t("apiMonitoring.placeholders.startDate"), t("apiMonitoring.placeholders.endDate")]}
        />
        <Button onClick={() => applyPreset("hour")}>{t("apiMonitoring.presets.last24Hours")}</Button>
        <Button onClick={() => applyPreset("day")}>{t("apiMonitoring.presets.last30Days")}</Button>
        <Button type="primary" onClick={onApply} disabled={apiKeyId == null}>
          {t("apiMonitoring.show")}
        </Button>
        <Button icon={<DownloadOutlined />} onClick={onExport} disabled={series.length === 0}>
          {t("apiMonitoring.csvExport")}
        </Button>
      </Flex>

      {apiKeyId == null ? (
        <Empty description={t("apiMonitoring.emptySelect")} />
      ) : (
        <Spin spinning={statsLoading}>
          {!hasData ? (
            <Empty description={t("apiMonitoring.emptyNoData")} />
          ) : (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Card title={t("apiMonitoring.cards.calls")}>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={buckets}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => axisLabel(v, granularity)} />
                    <YAxis allowDecimals={false} />
                    <Tooltip labelFormatter={(v) => axisLabel(String(v), granularity)} />
                    <Legend />
                    <Line type="monotone" dataKey="total" name={t("apiMonitoring.series.calls")} stroke="#1677ff" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              <Card title={t("apiMonitoring.cards.errorRate")}>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={buckets}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => axisLabel(v, granularity)} />
                    <YAxis unit="%" domain={[0, 100]} />
                    <Tooltip
                      labelFormatter={(v) => axisLabel(String(v), granularity)}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, t("apiMonitoring.series.errorRate")]}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="errorRate" name={t("apiMonitoring.series.errorRate")} stroke="#f5222d" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              <Card title={t("apiMonitoring.cards.rateLimited")}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={buckets}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => axisLabel(v, granularity)} />
                    <YAxis allowDecimals={false} />
                    <Tooltip labelFormatter={(v) => axisLabel(String(v), granularity)} />
                    <Legend />
                    <Bar dataKey="rateLimited" name={t("apiMonitoring.series.rateLimited")} fill="#faad14" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card title={t("apiMonitoring.cards.statusBreakdown")}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={buckets}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => axisLabel(v, granularity)} />
                    <YAxis allowDecimals={false} />
                    <Tooltip labelFormatter={(v) => axisLabel(String(v), granularity)} />
                    <Legend />
                    {STATUS_CLASSES.map((sc) => (
                      <Bar key={sc} dataKey={sc} name={sc} stackId="status" fill={STATUS_COLORS[sc]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Typography.Text type="secondary">
                {t("apiMonitoring.footerNote")}
              </Typography.Text>
            </Space>
          )}
        </Spin>
      )}
    </div>
  );
}

export default function ApiMonitoringPage() {
  return (
    <ApiAccessStatsProvider>
      <ApiMonitoringPageInner />
    </ApiAccessStatsProvider>
  );
}
